import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePriceRule } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle } from 'lucide-react';
import type { PriceRule } from '@/types/price-rules';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    rule: PriceRule | null;
    onSuccess?: () => void;
}

export default function DeleteRuleDialog({ open, onOpenChange, rule, onSuccess }: Props) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: () => deletePriceRule(rule!.id),
        onSuccess: () => {
            toast({ title: 'Regra excluída', description: 'A regra foi excluída com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['price-rules'] });
            onOpenChange(false);
            onSuccess?.();
        },
        onError: (error: any) => {
            toast({ variant: 'destructive', title: 'Erro ao excluir', description: error.response?.data?.detail || 'Erro ao excluir.' });
        },
    });

    if (!rule) return null;

    const formatSegmentation = () => {
        const parts = [];
        if (rule.country !== '*') parts.push(rule.country);
        if (rule.ad_unit !== '*') parts.push(rule.ad_unit);
        if (rule.device !== '*') parts.push(rule.device);
        if (rule.custom_key) parts.push(`${rule.custom_key}=${rule.custom_value}`);
        return parts.length > 0 ? parts.join(' • ') : 'Regra genérica';
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />Excluir Regra
                    </DialogTitle>
                    <DialogDescription>Tem certeza que deseja excluir esta regra?</DialogDescription>
                </DialogHeader>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">{formatSegmentation()}</p>
                    <p className="text-sm text-gray-500 mt-1">Price: R$ {rule.price.toFixed(2)} | Meta PMR: {rule.pmr_target}%</p>
                </div>
                <div className="flex gap-3 mt-6">
                    <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={deleteMutation.isPending}>
                        Cancelar
                    </Button>
                    <Button variant="destructive" className="flex-1" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
